import { toAbsolutePublicUrl } from "../../../services/mediaLibraryServices";

export const FALLBACK_EVENT_IMAGE = "/images/agenda-fallback.jpg";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 160) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(date) {
  if (!date) return null;
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function endOfDay(date) {
  if (!date) return null;
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

export function isSameDay(a, b) {
  if (!a || !b) return false;

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatTime(value) {
  const date = parseDate(value);
  if (!date) return "";

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFullDate(value) {
  const date = parseDate(value);
  if (!date) return "";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function formatEventDateParts(value) {
  const date = parseDate(value);

  if (!date) {
    return {
      day: "--",
      month: "---",
      year: "",
      time: "",
    };
  }

  return {
    day: String(date.getDate()).padStart(2, "0"),
    month: date
      .toLocaleString("fr-FR", { month: "short" })
      .replace(".", "")
      .toUpperCase(),
    year: String(date.getFullYear()),
    time: formatTime(date),
  };
}

export function getEventPeriodLabel(event) {
  const start = parseDate(event?.startDate || event?.start_date);
  const end = parseDate(event?.endDate || event?.end_date);
  const allDay = Boolean(event?.allDay ?? event?.all_day);

  if (!start) return "Date à confirmer";

  const startLabel = formatFullDate(start);

  if (!end || isSameDay(start, end)) {
    if (allDay) return startLabel;

    const startTime = formatTime(start);
    const endTime = end ? formatTime(end) : "";

    if (startTime && endTime && startTime !== endTime) {
      return `${startLabel} · ${startTime} - ${endTime}`;
    }

    if (startTime) {
      return `${startLabel} · ${startTime}`;
    }

    return startLabel;
  }

  const endLabel = formatFullDate(end);

  if (allDay) {
    return `Du ${startLabel} au ${endLabel}`;
  }

  const startTime = formatTime(start);
  const endTime = formatTime(end);

  if (startTime && endTime) {
    return `Du ${startLabel} à ${startTime} au ${endLabel} à ${endTime}`;
  }

  return `Du ${startLabel} au ${endLabel}`;
}

export function getEventStatus(start, end) {
  const now = new Date();
  const startDate = parseDate(start);
  const endDate = parseDate(end) || startDate;

  if (!startDate) return "unknown";

  const startDay = startOfDay(startDate);
  const endDay = endOfDay(endDate);

  if (endDay < now) return "past";
  if (startDay <= now && endDay >= now) return "ongoing";
  return "upcoming";
}

export function getEventStatusLabel(status) {
  const labels = {
    upcoming: "À venir",
    ongoing: "En cours",
    past: "Terminé",
    unknown: "Date à confirmer",
  };

  return labels[status] || labels.unknown;
}

export function resolveEventImage(event) {
  const candidate = event?.photo_vignette || event?.image_url || "";
  if (!candidate) return FALLBACK_EVENT_IMAGE;

  return toAbsolutePublicUrl(candidate) || FALLBACK_EVENT_IMAGE;
}

export function getEventDescriptionRaw(event) {
  return (
    event?.description_html ||
    event?.description ||
    event?.content ||
    event?.texte ||
    ""
  );
}

export function getEventLocation(event) {
  return (
    event?.lieu ||
    event?.location ||
    [event?.adresse, event?.ville].filter(Boolean).join(" - ") ||
    ""
  );
}

export function normalizeEvent(event) {
  const title = event?.title || "Événement";
  const subtitle = event?.subtitle || "";
  const category = event?.categorie || "";
  const descriptionRaw = getEventDescriptionRaw(event);
  const descriptionText = stripHtml(descriptionRaw);
  const location = getEventLocation(event);
  const allDay = Boolean(event?.all_day);

  const status = getEventStatus(event?.start_date, event?.end_date);

  const normalized = {
    id: event?.id,
    raw: event,

    title,
    subtitle,
    category,
    descriptionRaw,
    descriptionText,
    descriptionPreview: truncateText(descriptionText, 155),

    startDate: event?.start_date || "",
    endDate: event?.end_date || "",
    createdAt: event?.created_at || event?.createdAt || "",

    allDay,
    dateParts: formatEventDateParts(event?.start_date),
    endDateParts: event?.end_date ? formatEventDateParts(event.end_date) : null,

    periodLabel: getEventPeriodLabel({
      startDate: event?.start_date,
      endDate: event?.end_date,
      allDay,
    }),

    status,
    statusLabel: getEventStatusLabel(status),

    location,
    externalLink: event?.external_link || "",

    image: resolveEventImage(event),
  };

  normalized.searchIndex = normalizeSearchText(
    [
      normalized.title,
      normalized.subtitle,
      normalized.category,
      normalized.descriptionText,
      normalized.location,
      normalized.periodLabel,
      event?.ville,
      event?.adresse,
      event?.external_link,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return normalized;
}

export function eventMatchesDateRange(event, dateFrom, dateTo) {
  const start = parseDate(event.startDate);
  const end = parseDate(event.endDate) || start;

  if (!start) return true;

  const from = dateFrom ? startOfDay(parseDate(dateFrom)) : null;
  const to = dateTo ? endOfDay(parseDate(dateTo)) : null;

  if (from && end < from) return false;
  if (to && start > to) return false;

  return true;
}

export function sortEvents(events, sort, dir) {
  const direction = dir === "DESC" ? -1 : 1;

  return [...events].sort((a, b) => {
    let valueA;
    let valueB;

    if (sort === "title") {
      valueA = normalizeSearchText(a.title);
      valueB = normalizeSearchText(b.title);
      return valueA.localeCompare(valueB, "fr") * direction;
    }

    if (sort === "end_date") {
      valueA = parseDate(a.endDate)?.getTime() || 0;
      valueB = parseDate(b.endDate)?.getTime() || 0;
      return (valueA - valueB) * direction;
    }

    if (sort === "created_at") {
      valueA = parseDate(a.createdAt)?.getTime() || 0;
      valueB = parseDate(b.createdAt)?.getTime() || 0;
      return (valueA - valueB) * direction;
    }

    valueA = parseDate(a.startDate)?.getTime() || 0;
    valueB = parseDate(b.startDate)?.getTime() || 0;
    return (valueA - valueB) * direction;
  });
}
